 import { useState, useEffect } from 'react';
import {
    LuX, LuDownload, LuCopy, LuSearch, LuShieldCheck,
    LuLink, LuMail, LuHash, LuPhone
} from 'react-icons/lu';
import type { ScrapeJob, ScrapeResult } from '../../types';
import Table from './Table';
import Button from './Button';
import { exportToCSV, exportToExcel, API_URL } from '../../services/api';

interface LeadViewerProps {
    jobId: string | null;
    onClose: () => void;
}

// ─── Profile Detail Modal ──────────────────────────────────────────────────
function ProfileDetailModal({ profile, onClose }: { profile: ScrapeResult; onClose: () => void }) {
    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col">
                <div className="h-24 bg-gradient-to-r from-violet-600 via-fuchsia-500 to-orange-400 relative">
                    <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 rounded-full text-white transition-all backdrop-blur-sm">
                        <LuX size={20} />
                    </button>
                </div>
                <div className="px-8 pb-8 -mt-12 relative">
                    <div className="flex items-end justify-between mb-6">
                        <div className="relative">
                            <div className="w-24 h-24 rounded-[2rem] border-4 border-white bg-gray-100 overflow-hidden shadow-xl">
                                <div className="w-full h-full flex items-center justify-center text-violet-600 bg-violet-100 font-bold text-4xl uppercase">
                                    {profile.username.charAt(0)}
                                </div>
                            </div>
                            {profile.isVerified && (
                                <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white p-1 rounded-full border-2 border-white shadow-sm">
                                    <LuShieldCheck size={14} />
                                </div>
                            )}
                        </div>
                        <a
                            href={`https://instagram.com/${profile.username}`}
                            target="_blank"
                            rel="noreferrer"
                            className="px-5 py-2.5 bg-gray-900 text-white text-xs font-bold rounded-xl hover:bg-black transition-all shadow-lg"
                        >
                            Visit Instagram
                        </a>
                    </div>
                    <div className="space-y-1 mb-6">
                        <h3 className="text-2xl font-black text-gray-900 flex items-center gap-2">
                            {profile.fullName || profile.username}
                            {profile.isVerified && <LuShieldCheck className="text-blue-500" size={20} />}
                        </h3>
                        <p className="text-violet-600 font-bold">@{profile.username}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4 p-6 bg-slate-50 rounded-[2rem] border border-slate-100 mb-6">
                        <div className="text-center">
                            <p className="text-lg font-black text-gray-900">{(profile.mediaCount || 0).toLocaleString()}</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Posts</p>
                        </div>
                        <div className="text-center border-x border-slate-200">
                            <p className="text-lg font-black text-gray-900">{(profile.followerCount || 0).toLocaleString()}</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Followers</p>
                        </div>
                        <div className="text-center">
                            <p className="text-lg font-black text-gray-900">{(profile.followingCount || 0).toLocaleString()}</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Following</p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        {profile.biography && <p className="text-sm text-gray-600 leading-relaxed font-medium">{profile.biography}</p>}
                        <div className="space-y-3 pt-4 border-t border-gray-100">
                            {profile.category && (
                                <div className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><LuHash size={16} /></div>
                                    <span className="flex-1 truncate">{profile.category}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                                <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center"><LuMail size={16} /></div>
                                <span className="flex-1 truncate">{profile.email || 'No email found'}</span>
                                {profile.email && (
                                    <button onClick={() => navigator.clipboard.writeText(profile.email)} className="text-[10px] uppercase font-black tracking-widest text-emerald-600 hover:underline">Copy</button>
                                )}
                            </div>
                            {profile.contactPhoneNumber && (
                                <div className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                                    <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><LuPhone size={16} /></div>
                                    <span className="flex-1 truncate">{profile.contactPhoneNumber}</span>
                                </div>
                            )}
                            {profile.externalUrl && (
                                <div className="flex items-center gap-3 text-sm font-semibold text-gray-700">
                                    <div className="w-8 h-8 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-center"><LuLink size={16} /></div>
                                    <a href={profile.externalUrl.startsWith('http') ? profile.externalUrl : `https://${profile.externalUrl}`} target="_blank" rel="noreferrer" className="text-violet-600 hover:underline truncate flex-1">
                                        {profile.externalUrl.replace(/^https?:\/\//, '')}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="p-6 bg-gray-50 border-t border-gray-100">
                    <Button variant="secondary" className="w-full" onClick={onClose}>Close Profile</Button>
                </div>
            </div>
        </div>
    );
}

// ─── Main LeadViewer Component ─────────────────────────────────────────────
export default function LeadViewer({ jobId, onClose }: LeadViewerProps) {
    const [job, setJob] = useState<ScrapeJob | null>(null);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [copying, setCopying] = useState<string | null>(null);
    const [selectedProfile, setSelectedProfile] = useState<ScrapeResult | null>(null);
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 100; // render only this many rows at once — 10k rows froze the browser



    useEffect(() => {
        if (!jobId) { setJob(null); return; }
        let cancelled = false;
        let timer: ReturnType<typeof setInterval> | undefined;

        const fetchFullJob = async (showSpinner: boolean) => {
            if (showSpinner) setLoading(true);
            try {
                const token = localStorage.getItem('token');
                const res = await fetch(`${API_URL}/api/scrapes/${jobId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!res.ok) throw new Error('Failed to fetch job');
                const json = await res.json();
                if (cancelled) return;
                setJob(json.data);
                // While the job is still running, keep polling so each page's
                // leads stream in live; stop once it's no longer in-progress.
                if (json.data?.status !== 'in-progress' && timer) {
                    clearInterval(timer);
                    timer = undefined;
                }
            } catch (err) {
                console.error('LeadViewer fetch error:', err);
            } finally {
                if (showSpinner && !cancelled) setLoading(false);
            }
        };

        fetchFullJob(true);
        // Poll every 3s for live updates (refresh without the loading spinner).
        timer = setInterval(() => fetchFullJob(false), 3000);

        return () => {
            cancelled = true;
            if (timer) clearInterval(timer);
        };
    }, [jobId, API_URL]);

    if (!jobId) return null;

    // Check if this is a legacy job (no isolated columns exist, but legacy results array has data)
    const isLegacyJob = 
        (!job?.resultsEmails || job.resultsEmails.length === 0) &&
        (!job?.resultsProfiles || job.resultsProfiles.length === 0) &&
        (!job?.resultsLists || job.resultsLists.length === 0) &&
        ((job as any)?.results && (job as any).results.length > 0);

    // ── Determine the goal and pick the correct results bucket ──────────────
    // If it's a legacy job, force it to 'profiles' so the user can see all historical data.
    // Otherwise, use the saved scrapeGoal (defaults to emails).
    const goal = isLegacyJob ? 'profiles' : (job?.scrapeGoal || 'emails');

    const getResults = (): ScrapeResult[] => {
        if (!job) return [];
        if (goal === 'profiles' && job.resultsProfiles?.length) return job.resultsProfiles;
        if (goal === 'list'     && job.resultsLists?.length)    return job.resultsLists;
        if (goal === 'emails'   && job.resultsEmails?.length)   return job.resultsEmails;
        // Legacy fallback — jobs scraped before the new column system
        return (job as any).results || [];
    };

    const results = getResults();

    const filteredResults = results.filter(r =>
        (r.username || '').toLowerCase().includes(search.toLowerCase()) ||
        (r.email    || '').toLowerCase().includes(search.toLowerCase()) ||
        (r.fullName || '').toLowerCase().includes(search.toLowerCase())
    );

    // ── Pagination — only render PAGE_SIZE rows at a time (10k rows hung the page) ──
    const totalPages = Math.max(1, Math.ceil(filteredResults.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages - 1);
    const pageStart = safePage * PAGE_SIZE;
    const pageResults = filteredResults.slice(pageStart, pageStart + PAGE_SIZE);

    // ── Copy helper ─────────────────────────────────────────────────────────
    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopying(text);
        setTimeout(() => setCopying(null), 2000);
    };

    // ── Export — only the columns relevant to the goal ─────────────────────
    const handleExport = (type: 'csv' | 'excel') => {
        if (!job) return;

        let flatResults: Record<string, any>[];

        if (goal === 'emails') {
            flatResults = results.map(r => ({
                Username: r.username,
                FullName: r.fullName || '',
                Followers: r.followerCount || 0,
                Email: r.email || '',
                Verified: r.isVerified ? 'Yes' : 'No',
            }));
        } else if (goal === 'profiles') {
            flatResults = results.map(r => ({
                Username: r.username,
                FullName: r.fullName || '',
                Category: r.category || '',
                Biography: r.biography || '',
                Followers: r.followerCount || 0,
                Following: r.followingCount || 0,
                Posts: r.mediaCount || 0,
                Email: r.email || '',
                Phone: r.contactPhoneNumber || '',
                Website: r.externalUrl || '',
                Verified: r.isVerified ? 'Yes' : 'No',
                Business: r.isBusiness ? 'Yes' : 'No',
            }));
        } else {
            // list goal
            flatResults = results.map(r => ({
                Username: r.username,
                FullName: r.fullName || '',
                Followers: r.followerCount || 0,
                Following: r.followingCount || 0,
                Verified: r.isVerified ? 'Yes' : 'No',
            }));
        }

        const filename = `${goal}_${job.target}_${new Date().toISOString().slice(0, 10)}`;
        if (type === 'csv')   exportToCSV(flatResults, `${filename}.csv`);
        else                  exportToExcel(flatResults, `${filename}.xlsx`);
    };

    // ── Column definitions per goal ─────────────────────────────────────────
    const colIndex = {
        key: 'index',
        title: '#',
        render: (_: any, idx?: number) => <span className="text-gray-400 font-mono text-xs">{pageStart + (idx ?? 0) + 1}</span>,
        cellAlign: 'center' as const,
    };

    const colUser = {
        key: 'username',
        title: 'User',
        render: (row: ScrapeResult) => (
            <div className="flex items-center gap-3 min-w-[180px]">
                <button
                    onClick={() => setSelectedProfile(row)}
                    className="w-9 h-9 rounded-xl bg-violet-100 flex items-center justify-center text-violet-600 shrink-0 overflow-hidden border border-violet-200 hover:scale-105 transition-transform"
                >
                    <span className="font-bold text-[14px] uppercase">{row.username.charAt(0)}</span>
                </button>
                <div className="flex flex-col min-w-0">
                    <button onClick={() => setSelectedProfile(row)} className="font-bold text-gray-900 hover:text-violet-600 text-sm truncate text-left">
                        @{row.username}
                    </button>
                    <span className="text-[11px] text-gray-400 truncate">{row.fullName || '—'}</span>
                </div>
            </div>
        ),
    };

    const colFollowers = {
        key: 'followers',
        title: 'Followers',
        render: (row: ScrapeResult) => (
            <div className="flex flex-col">
                <span className="text-sm font-black text-gray-900">{(row.followerCount || 0).toLocaleString()}</span>
                {row.isVerified && <span className="text-[8px] font-black text-blue-500 uppercase tracking-wider mt-0.5">✓ Verified</span>}
            </div>
        ),
    };

    const colFollowing = {
        key: 'following',
        title: 'Following',
        render: (row: ScrapeResult) => (
            <span className="text-sm font-bold text-gray-700">{(row.followingCount || 0).toLocaleString()}</span>
        ),
    };

    const colEmail = {
        key: 'email',
        title: 'Email',
        render: (row: ScrapeResult) => (
            <div className="flex items-center justify-between group gap-2 min-w-[160px]">
                <span className="font-bold text-gray-800 text-xs truncate">
                    {row.email || <span className="text-gray-300 italic font-normal">No email</span>}
                </span>
                {row.email && (
                    <button
                        onClick={() => handleCopy(row.email)}
                        className={`p-1.5 rounded-lg transition-all shrink-0 ${copying === row.email ? 'bg-emerald-100 text-emerald-600' : 'opacity-0 group-hover:opacity-100 bg-gray-50 text-gray-400 hover:bg-violet-50 hover:text-violet-600'}`}
                    >
                        <LuCopy size={12} />
                    </button>
                )}
            </div>
        ),
    };

    const colCategory = {
        key: 'category',
        title: 'Category',
        render: (row: ScrapeResult) => (
            <span className="text-[10px] font-black text-violet-700 bg-violet-50 border border-violet-100 px-2.5 py-1 rounded-lg whitespace-nowrap uppercase tracking-wider">
                {row.category || 'User'}
            </span>
        ),
    };

    const colBio = {
        key: 'biography',
        title: 'Biography',
        render: (row: ScrapeResult) => (
            <span className="text-[11px] text-gray-600 line-clamp-2 min-w-[160px] max-w-[280px] leading-relaxed">
                {row.biography || <span className="text-gray-300 italic">No bio</span>}
            </span>
        ),
    };

    const colStats = {
        key: 'stats',
        title: 'Posts / Following',
        render: (row: ScrapeResult) => (
            <div className="flex flex-col text-[10px] font-bold text-gray-500">
                <span>Posts: {(row.mediaCount || 0).toLocaleString()}</span>
                <span>Following: {(row.followingCount || 0).toLocaleString()}</span>
            </div>
        ),
    };

    const colPhone = {
        key: 'phone',
        title: 'Phone',
        render: (row: ScrapeResult) => (
            <span className="text-xs font-bold text-gray-700 whitespace-nowrap">
                {row.contactPhoneNumber || <span className="text-gray-300">—</span>}
            </span>
        ),
    };

    const colWebsite = {
        key: 'website',
        title: 'Website',
        render: (row: ScrapeResult) =>
            row.externalUrl
                ? <a href={row.externalUrl.startsWith('http') ? row.externalUrl : `https://${row.externalUrl}`} target="_blank" rel="noreferrer" className="text-xs font-black text-violet-600 hover:underline truncate max-w-[140px] block">{row.externalUrl.replace(/^https?:\/\//, '')}</a>
                : <span className="text-gray-300 text-xs">—</span>,
    };

    // Strict column sets per goal
    const columns =
        goal === 'emails'   ? [colIndex, colUser, colFollowers, colEmail] :
        goal === 'profiles' ? [colIndex, colUser, colCategory, colBio, colFollowers, colStats, colEmail, colPhone, colWebsite] :
        /* list */            [colIndex, colUser, colFollowers, colFollowing];

    const goalLabel =
        goal === 'emails'   ? 'Email Discovery' :
        goal === 'profiles' ? 'Profile Details' :
        'Followers / Following';

    const goalColor =
        goal === 'emails'   ? 'bg-emerald-100 text-emerald-700' :
        goal === 'profiles' ? 'bg-violet-100 text-violet-700' :
        'bg-blue-100 text-blue-700';

    return (
        <>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                <div className="bg-white rounded-[2.5rem] w-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">

                    {/* Header */}
                    <div className="px-8 py-6 border-b border-gray-100 flex items-center justify-between bg-white">
                        <div>
                            <h2 className="text-xl font-black text-gray-900 flex items-center gap-3">
                                View Leads
                                <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest ${goalColor}`}>
                                    {goalLabel}
                                </span>
                                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-bold">
                                    {job?.target || '...'}
                                </span>
                            </h2>
                            <p className="text-sm text-gray-400 mt-1 font-medium">
                                {loading ? 'Loading data...' : `${filteredResults.length} result${filteredResults.length !== 1 ? 's' : ''} found`}
                            </p>
                        </div>
                        <button onClick={onClose} className="p-3 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-900">
                            <LuX size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-24 gap-4">
                                <div className="w-12 h-12 border-4 border-violet-100 border-t-violet-600 rounded-full animate-spin" />
                                <p className="text-gray-400 font-bold animate-pulse">Loading results...</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Toolbar */}
                                <div className="flex items-center gap-4 justify-between">
                                    <div className="relative w-80">
                                        <LuSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                        <input
                                            type="text"
                                            placeholder="Search by username, name or email..."
                                            value={search}
                                            onChange={e => { setSearch(e.target.value); setPage(0); }}
                                            className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none transition-all font-medium"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button variant="accent" size="sm" onClick={() => handleExport('csv')}>
                                            <LuDownload size={14} className="mr-1" /> CSV
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => handleExport('excel')}>
                                            <LuDownload size={14} className="mr-1" /> Excel
                                        </Button>
                                    </div>
                                </div>

                                {/* Table — only the current page of rows is rendered */}
                                <div className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                                    <Table columns={columns} data={pageResults} />
                                    {filteredResults.length === 0 && (
                                        <div className="py-20 text-center bg-gray-50/50">
                                            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300">
                                                <LuSearch size={28} />
                                            </div>
                                            <h3 className="font-bold text-gray-700">No results found</h3>
                                            <p className="text-gray-400 text-sm mt-1">
                                                {results.length === 0
                                                    ? 'This scrape job has no data yet. It may still be processing.'
                                                    : 'No results match your search.'}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                {/* Pagination */}
                                {filteredResults.length > PAGE_SIZE && (
                                    <div className="flex items-center justify-between pt-1">
                                        <span className="text-xs text-gray-400 font-semibold">
                                            Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredResults.length)} of {filteredResults.length.toLocaleString()}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <Button variant="outline" size="sm" disabled={safePage === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</Button>
                                            <span className="text-xs font-bold text-gray-600">Page {safePage + 1} / {totalPages}</span>
                                            <Button variant="outline" size="sm" disabled={safePage >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>Next</Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-8 py-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between">
                        <div className="flex items-center gap-6">
                            <div>
                                <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest block">Total</span>
                                <span className="text-xl font-black text-gray-900">{results.length}</span>
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest block">Scrape Type</span>
                                <span className="text-sm font-bold text-violet-600">{goalLabel}</span>
                            </div>
                            <div>
                                <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest block">Target</span>
                                <span className="text-sm font-bold text-gray-700">{job?.target || '—'}</span>
                            </div>
                        </div>
                        <Button variant="secondary" onClick={onClose} className="px-8 rounded-xl">Close</Button>
                    </div>
                </div>
            </div>

            {selectedProfile && (
                <ProfileDetailModal profile={selectedProfile} onClose={() => setSelectedProfile(null)} />
            )}
        </>
    );
}
